// ./src/utils/api/CategoryService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";

// GraphQL queries
const CATEGORY_LIST_QUERY = gql`
  query CategoryList($filter: CategoryFilter, $params: PaginationAndSorting) {
    categoryList(filter: $filter, params: $params) {
      code
      data {
        id
        name
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CATEGORY_GET_QUERY = gql`
  query CategoryGet($id: ID) {
    categoryGet(id: $id) {
      code
      data {
        id
        name
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CATEGORY_GET_MANY_QUERY = gql`
  query CategoryGetMany($ids: [ID]) {
    categoryGetMany(ids: $ids) {
      code
      data {
        id
        name
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const CATEGORY_CREATE_MUTATION = gql`
  mutation CategoryCreate($params: CategoryInput) {
    categoryCreate(params: $params) {
      code
      data {
        id
        name
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CATEGORY_UPDATE_MUTATION = gql`
  mutation CategoryUpdate($where: CategoryWhereInput, $params: CategoryInput) {
    categoryUpdate(where: $where, params: $params) {
      code
      data {
        id
        name
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CATEGORY_REMOVE_MUTATION = gql`
  mutation CategoryRemove($where: CategoryWhereInput) {
    categoryRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CATEGORY_REMOVE_MANY_MUTATION = gql`
  mutation CategoryRemoveMany($where: [CategoryWhereInput]) {
    categoryRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types

export interface Category {
  id?: string | undefined;
  name?: string | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
}

export interface CategoryInput {
  name?: string | null | undefined;
}

export interface CategoryFilter {
  id?: string[] | null | undefined;
  name?: string | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface CategoryWhereInput {
  id?: string | undefined;
}

/**
 * Category service that handles all category-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class CategoryService {
  /**
   * Get a list of categories with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: CategoryFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CATEGORY_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single category by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(CATEGORY_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple categories by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(CATEGORY_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new category
   */
  static async create(params: CategoryInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(CATEGORY_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing category
   */
  static async update(
    where: CategoryWhereInput,
    params: CategoryInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(CATEGORY_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a category
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(CATEGORY_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple categories
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      CATEGORY_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
